import { useCallback, useEffect, useLayoutEffect, useRef, type RefObject } from 'react';

const useClientLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

/** Animate the existing visual region without remounting focus or scroll-video state. */
export function useGallerySwapMotion(
  visualRef: RefObject<HTMLElement | null>,
  activeIndex: number,
) {
  const previousIndex = useRef(activeIndex);
  const direction = useRef(1);

  const prepareSwap = useCallback((requestedDelta: number) => {
    direction.current = requestedDelta < 0 ? -1 : 1;
  }, []);

  useClientLayoutEffect(() => {
    const changed = previousIndex.current !== activeIndex;
    previousIndex.current = activeIndex;
    const visual = visualRef.current;
    if (!changed || !visual || typeof visual.animate !== 'function') return;

    const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (preference.matches) return;

    const poster = visual.querySelector('img');
    let animation: Animation | undefined;
    let disposed = false;
    const start = () => {
      if (disposed || preference.matches) return;
      animation?.cancel();
      animation = visual.animate(
        [
          { opacity: 0, transform: `translateX(${direction.current * 6}px)` },
          { opacity: 1, transform: 'translateX(0)' },
        ],
        { duration: 220, easing: 'cubic-bezier(0.2, 0.7, 0.3, 1)' },
      );
    };
    // An uncached slide stays visible while loading; reveal it when its image is ready.
    if (!poster || (poster.complete && poster.naturalWidth > 0)) start();
    else if (!poster.complete) poster.addEventListener('load', start, { once: true });
    const syncPreference = () => {
      if (preference.matches) animation?.cancel();
    };
    preference.addEventListener('change', syncPreference);

    return () => {
      disposed = true;
      poster?.removeEventListener('load', start);
      preference.removeEventListener('change', syncPreference);
      animation?.cancel();
    };
  }, [activeIndex, visualRef]);

  return prepareSwap;
}
